import { cn } from '@/lib/utils';
import type { WorldSlug } from '@/lib/worlds';

/**
 * Hero-image-over-label welt tile for the Start-Page "Welten entdecken"
 * row. Distinct visual from `WorldSelectionSheet`'s emoji+row pattern —
 * this is the full-bleed hero format Sönke designed in node 8:5536.
 *
 * Hero image source is parameterized (`coverSrc`) so the Batch 7
 * gradient-placeholder approach (per Gate-1 D1 override) can be swapped
 * for Sönke-designed PNGs later with zero API change. Pass an asset URL
 * (`/covers/welten/aa.png`) when real assets land, or a data-URL /
 * gradient rendered via the `placeholder` slot for launch.
 *
 * Layout: aspect-224/144 image on top, label `px-3 py-4` below on
 * `bg-blue-bg` tile. `rounded-lg overflow-clip`.
 */
export interface WeltCardProps {
  slug: WorldSlug;
  /** Display name — resolved from `WORLDS[slug].name` by caller. */
  name: string;
  /** Hero image URL. Omitting falls back to `placeholder` slot. */
  coverSrc?: string;
  /** Rendered inside the hero slot when `coverSrc` is not provided
   *  (Batch 7 launch: gradient div per world). */
  placeholder?: React.ReactNode;
  onClick: () => void;
  className?: string;
  /** Optional data-testid forwarded to the outer button. */
  testId?: string;
}

export function WeltCard({
  slug,
  name,
  coverSrc,
  placeholder,
  onClick,
  className,
  testId,
}: WeltCardProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'flex flex-1 flex-col overflow-hidden rounded-lg bg-blue-bg text-left transition-transform hover:scale-[1.01] focus-visible:outline-2 focus-visible:outline-dream-navy/50',
        className,
      )}
      data-testid={testId}
      data-world-slug={slug}
    >
      <div className="relative aspect-[224/144] w-full">
        {coverSrc ? (
          <img src={coverSrc} alt="" className="h-full w-full object-cover" draggable={false} />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center">
            {placeholder ?? <div className="h-full w-full bg-dream-navy/10" />}
          </div>
        )}
      </div>
      <div className="flex w-full flex-col items-start px-3 py-4">
        <p className="w-full text-xs font-medium leading-3 text-dream-navy">{name}</p>
      </div>
    </button>
  );
}
